'use client';
import { useStickyVisibility } from '@shared/hooks';
import { useMediaQuery } from '@shared/utils';
import Link from 'next/link';
import { type ReactNode, type RefObject, useRef } from 'react';
import { Button } from '../../../../../shadcn/components/Button/Button';
import { useInboxMessageMutations } from '../../hooks/useInboxMessageMutations/useInboxMessageMutations';

type CtaProps = {
  children: ReactNode;
  id: string;
  href: string;
};

type CtaButtonProps = Omit<CtaProps, 'id'> & {
  className: string;
  onClick: () => void;
  ref?: RefObject<HTMLButtonElement | null>;
};

function CtaButton({ className, ref, onClick, href, children }: CtaButtonProps) {
  return (
    <Button ref={ref} variant="primary" className={className} asChild onClick={onClick}>
      <Link href={href}>{children}</Link>
    </Button>
  );
}

/**
 * Cta is component that renders click button for inbox.
 * Inside the component, there is logic, to show sticky button on mobile (only) when Cta goes out of viewport.
 *
 * @param children - renders as action button text
 * @param href - link href
 * @param id - message id
 * @returns renderd and styled action button
 */
function Cta({ children, id, ...props }: CtaProps) {
  const targetRef = useRef<HTMLButtonElement>(null);
  const isDesktopView = useMediaQuery(`(width >= 640px)`);
  const { isVisible: shouldShowSticky } = useStickyVisibility({ targetRef, disabled: isDesktopView });
  const { clickMessageMutation } = useInboxMessageMutations(id);

  const handleCtaClick = () => {
    clickMessageMutation.mutate(undefined);
  };

  return (
    <>
      <CtaButton ref={targetRef} className="mt-5" onClick={handleCtaClick} {...props}>
        {children}
      </CtaButton>
      {shouldShowSticky ? (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-5 bg-container-base shadow-lg rounded-t-lg">
          <CtaButton className="flex-1 w-full" onClick={handleCtaClick} {...props}>
            {children}
          </CtaButton>
        </div>
      ) : null}
    </>
  );
}

export { Cta, CtaButton };
